import {isPlatformBrowser, NgOptimizedImage} from "@angular/common";
import {HttpErrorResponse, HttpStatusCode} from "@angular/common/http";
import {
  Component,
  computed,
  inject,
  injectAsync,
  input,
  linkedSignal,
  PLATFORM_ID,
  signal,
  type WritableSignal,
} from "@angular/core";
import {FormsModule} from "@angular/forms";
import {Router, RouterLink} from "@angular/router";
import {firstValueFrom, from, of, switchMap} from "rxjs";
import type {AppRoute} from "../../../app.routes";
import {BootstrapFormControlValidationClasses} from "../../../directives/bootstrap-form-control-validation-classes";
import {BootstrapIcon} from "../../../directives/bootstrap-icon";
import {RouterLinkAppRoute} from "../../../directives/router-link-app-route";
import {ValueEqualValidator, ValueNotEqualValidator} from "../../../directives/value-comparison-validator";
import {changeLanguageIfNeeded} from "../../../helpers/LanguageChanges";
import {rxSafeResource} from "../../../helpers/ResourceHelpers";
import {completionOf} from "../../../helpers/RxJsOperators";
import {ControlValidationErrors} from "../../../layout/control-validation-errors/control-validation-errors";
import {ButtonLoadingIndicator} from "../../../layout/loading-indicator/loading-indicator";
import {PageTitle} from "../../../layout/page-title/page-title";
import {LANGUAGE_CODE} from "../../../models/i18n/Language";
import {CredentialsStorage} from "../../../services/credentials-storage";

export enum PasswordFormType {
  Login,
  ForgotPassword,
  ChangePassword,
  ResetPassword,
}

@Component({
  selector: "rf-password-form",
  templateUrl: "./password-form.html",
  styleUrl: "./password-form.scss",
  imports: [
    BootstrapFormControlValidationClasses,
    BootstrapIcon,
    ButtonLoadingIndicator,
    ControlValidationErrors,
    FormsModule,
    NgOptimizedImage,
    PageTitle,
    RouterLink,
    RouterLinkAppRoute,
    ValueEqualValidator,
    ValueNotEqualValidator,
  ],
})
export class PasswordForm {
  protected readonly PasswordFormType = PasswordFormType;

  private readonly platformId = inject(PLATFORM_ID);
  private readonly currentLanguage = inject(LANGUAGE_CODE);
  private readonly router = inject(Router);

  private readonly credentialsStorage = inject(CredentialsStorage);

  private readonly authApi = injectAsync(() => import("../../../services/api/auth-api").then(m => m.AuthApi));
  private readonly usersApi = injectAsync(() => import("../../../services/api/users/users-api").then(m => m.UsersApi));
  private readonly toasts = injectAsync(() => import("../../../services/toasts").then(m => m.Toasts));

  public readonly formType = input.required<PasswordFormType>();

  // Provided via query params -> thus the alias
  // eslint-disable-next-line @angular-eslint/no-input-rename
  public readonly returnUrl = input<string>(undefined, {alias: "return-url"});
  // These are only provided for reset password
  // eslint-disable-next-line @angular-eslint/no-input-rename
  public readonly resetToken = input<string>(undefined, {alias: "reset-token"});
  // eslint-disable-next-line @angular-eslint/no-input-rename
  public readonly userEmail = input<string>(undefined, {alias: "user-email"});

  protected readonly title = computed(() => {
    switch (this.formType()) {
      case PasswordFormType.Login:
        return $localize`Please Login`;
      case PasswordFormType.ForgotPassword:
        return $localize`Forgot Password`;
      case PasswordFormType.ChangePassword:
        return $localize`Change Password`;
      case PasswordFormType.ResetPassword:
        return $localize`Set Password`;
    }
  });

  protected readonly description = computed(() => {
    switch (this.formType()) {
      case PasswordFormType.Login:
        return undefined;
      case PasswordFormType.ForgotPassword:
        return $localize`Please enter your registered email into the form below. An email will be sent to your address with further instructions.`;
      case PasswordFormType.ChangePassword:
      case PasswordFormType.ResetPassword:
        return $localize`Here you can enter a new password. Please take note of the complexity criteria.`;
    }
  });
  protected readonly buttonLabel = computed(() => {
    switch (this.formType()) {
      case PasswordFormType.Login:
        return $localize`Login`;
      case PasswordFormType.ForgotPassword:
        return $localize`Send`;
      case PasswordFormType.ChangePassword:
      case PasswordFormType.ResetPassword:
        return $localize`Save`;
    }
  });

  protected readonly showImage = computed(() =>
    this.formType() === PasswordFormType.Login || this.formType() === PasswordFormType.ForgotPassword);
  protected readonly showEmail = computed(() =>
    this.formType() === PasswordFormType.Login || this.formType() === PasswordFormType.ForgotPassword);
  protected showNewPasswordField = computed(() =>
    this.formType() === PasswordFormType.ChangePassword || this.formType() === PasswordFormType.ResetPassword);

  protected readonly specialCharacters = "! @ # $ % ^ & * ( ) \\ - _ = + { } ; : , < . >";
  protected readonly newPasswordPattern = /^(?=.*[A-Z])(?=.*[a-z])(?=.*\d)(?=.*[!@#$%^&*()\\\-_=+{};:,<.>]).+$/;

  protected readonly currentUser = rxSafeResource({
    params: () => !this.showEmail() && this.credentialsStorage.nonExpiredCredentials()?.isExpired === false,
    stream: ({params: shouldLoad}) => shouldLoad
      ? from(this.usersApi()).pipe(switchMap(api => api.getContactableMe()))
      : of(undefined),
  });
  private readonly currentUserEmail = computed(() => this.currentUser.valueOrDefault()?.email);

  protected readonly email = linkedSignal(() => this.currentUserEmail() ?? this.userEmail() ?? "");
  protected readonly password = signal("");
  protected readonly showPassword = signal(false);
  protected readonly newPassword = signal("");
  protected readonly showNewPassword = signal(false);
  protected readonly repeatPassword = signal("");
  protected readonly showRepeatPassword = signal(false);

  protected readonly isRequesting = signal(false);

  protected toggle(signal: Readonly<WritableSignal<boolean>>): void {
    signal.update(value => !value);
  }

  protected async submit(): Promise<void> {
    this.isRequesting.set(true);
    try {
      switch (this.formType()) {
        case PasswordFormType.Login: {
          try {
            const tokenResponse = await firstValueFrom((await this.authApi()).login(
              this.email(), this.password(), [HttpStatusCode.Unauthorized]));
            if (tokenResponse.needsPasswordChange) {
              (await this.toasts()).showInfo($localize`Password change required`,
                $localize`You need to change your password first.`);
              await this.router.navigate(["/change-password"] satisfies AppRoute, {queryParams: {"return-url": this.returnUrl()}});
            } else {
              const returnUrl = this.returnUrl();
              if (returnUrl === undefined || !await this.router.navigateByUrl(returnUrl))
                await this.router.navigate(["/dashboard"] satisfies AppRoute);
            }
            // We do this _after_ all the navigation above. This should take care of being on the right page before we switch the language.
            if (isPlatformBrowser(this.platformId))
              changeLanguageIfNeeded(this.currentLanguage, tokenResponse.language.code, window);
          } catch (error) {
            if (error instanceof HttpErrorResponse && error.status === HttpStatusCode.Unauthorized.valueOf())
              (await this.toasts()).showError($localize`Incorrect email or password`);
            else
              throw error;
          }
          break;
        }
        case PasswordFormType.ForgotPassword:
          await completionOf((await this.authApi()).forgotPassword(this.email(), this.returnUrl()));
          (await this.toasts()).showSuccess($localize`Email sent`,
            $localize`If the email address you provided is registered, you should receive an email shortly.`);
          break;
        case PasswordFormType.ChangePassword: {
          await completionOf((await this.authApi()).changePassword(this.password(), this.newPassword()));
          (await this.toasts()).showSuccess($localize`Password changed`, $localize`Your new password has been saved.`);
          const returnUrl = this.returnUrl();
          if (returnUrl !== undefined) await this.router.navigateByUrl(returnUrl);
          break;
        }
        case PasswordFormType.ResetPassword: {
          const userEmail = this.userEmail();
          const resetToken = this.resetToken();
          if (userEmail === undefined || resetToken === undefined) {
            (await this.toasts()).showError($localize`Invalid link`,
              $localize`The link you used to reset your password is invalid. Please request a new one.`);
            await this.router.navigate(["/forgot-password"] satisfies AppRoute, {queryParams: {"return-url": this.returnUrl()}});
            return;
          }
          await completionOf((await this.authApi()).resetPassword(userEmail, resetToken, this.newPassword()));
          (await this.toasts()).showSuccess($localize`Password reset`,
            $localize`Your new password has been saved. You may now login with this password.`);
          await this.router.navigate(["/login"] satisfies AppRoute, {queryParams: {"return-url": this.returnUrl()}});
          break;
        }
      }
    } finally {
      this.isRequesting.set(false);
    }
  }
}
